import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FaArrowLeft, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import OurCompanies from "../Home/OurCompanies";
import "./IconicBlogDetails.css";
import { blogPosts } from "./blogData";

/* ---------- Simple Image Slider ---------- */
const ImageSlider = ({ images = [], alt = "Blog image" }) => {
  const [current, setCurrent] = useState(0);

  if (!images || images.length === 0) return null;

  const prev = () => setCurrent((c) => (c === 0 ? images.length - 1 : c - 1));
  const next = () => setCurrent((c) => (c === images.length - 1 ? 0 : c + 1));

  return (
    <div className="blog-slider">
      <div className="blog-slider-main">
        <img src={images[current]} alt={`${alt} ${current + 1}`} />

        {images.length > 1 && (
          <>
            <button className="blog-slider-btn blog-slider-prev" onClick={prev}>
              <FaChevronLeft />
            </button>
            <button className="blog-slider-btn blog-slider-next" onClick={next}>
              <FaChevronRight />
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="blog-slider-dots">
          {images.map((_, idx) => (
            <button
              key={idx}
              className={`blog-slider-dot ${idx === current ? "active" : ""}`}
              onClick={() => setCurrent(idx)}
            />
          ))}
        </div>
      )}

      {images.length > 1 && (
        <div className="blog-slider-counter">
          {current + 1} / {images.length}
        </div>
      )}
    </div>
  );
};

/* ---------- Main Component ---------- */
const IconicBlogDetails = () => {
  const { slug } = useParams();
  const post = blogPosts.find((item) => item.slug === slug);

  if (!post) {
    return (
      <div className="blog-details-not-found">
        <h2>Article not found</h2>
        <p>
          The article you are looking for is unavailable or may have been moved.
        </p>
        <Link to="/blogs" className="blog-details-back-link">
          Back to all blogs
        </Link>
      </div>
    );
  }

  const relatedPosts = blogPosts
    .filter((item) => item.id !== post.id && item.category === post.category)
    .slice(0, 2);

  return (
    <div className="blog-details-page">
      {/* ========== HERO ========== */}
      <section className="blog-details-hero">
        <div className="blog-details-hero-shell">
          <div className="blog-details-hero-copy">
            <Link to="/blogs" className="blog-details-back-btn">
              <FaArrowLeft />
              Back to blogs
            </Link>
            <span className="blog-details-badge">{post.categoryLabel}</span>
            <h1>{post.title}</h1>
            <p>{post.intro}</p>
            <div className="blog-details-meta">
              <span>{post.author}</span>
              <span className="blog-details-dot">&bull;</span>
              <span>{post.date}</span>
              <span className="blog-details-dot">&bull;</span>
              <span>{post.readTime}</span>
            </div>
          </div>

          <div className="blog-details-visual-card">
            <div className="blog-details-hero-overlay" />
            <img
              src={post.image}
              alt={post.title}
              className="blog-details-hero-image"
            />
            <div className="blog-details-visual-note">
              <span>{post.categoryLabel}</span>
              <strong>Iconic Unity Group Insight</strong>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CONTENT ========== */}
      <section className="blog-details-shell">
        <div className="blog-details-main">
          <article className="blog-details-article">
            {/* Overview */}
            <div className="blog-details-intro-card">
              <span className="blog-details-section-kicker">Overview</span>
              <h2>Article Overview</h2>
              <p>{post.excerpt}</p>
            </div>

            {/* Highlights */}
            <div className="blog-details-highlights">
              {post.highlights.map((item) => (
                <div key={item} className="blog-details-highlight-item">
                  <span className="blog-details-highlight-bullet" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            {/* Post-level images (if any) */}
            {post.images && post.images.length > 0 && (
              <div className="blog-details-post-gallery">
                <ImageSlider images={post.images} alt={post.title} />
              </div>
            )}

            {/* Sections with optional image slider */}
            <div className="blog-details-sections">
              {post.sections.map((section) => (
                <section key={section.heading} className="blog-details-section">
                  <h3>{section.heading}</h3>
                  <p>{section.body}</p>

                  {/* Multiple images slider for this section */}
                  {section.images && section.images.length > 0 && (
                    <div className="blog-details-section-gallery">
                      <ImageSlider
                        images={section.images}
                        alt={section.heading}
                      />
                    </div>
                  )}
                </section>
              ))}
            </div>
          </article>

          {/* Sidebar */}
          <aside className="blog-details-sidebar">
            <div className="blog-details-side-card">
              <span className="blog-details-side-kicker">Quick takeaway</span>
              <h3>Why it matters</h3>
              <p>{post.relatedNote}</p>
            </div>

            <div className="blog-details-side-card">
              <span className="blog-details-side-kicker">Explore more</span>
              <h3>Need more insights?</h3>
              <p>
                Explore more stories from Iconic Unity Group and discover how
                each concern is creating impact.
              </p>
              <Link to="/blogs" className="blog-details-primary-link">
                Explore all articles
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="blog-details-related-wrap">
          <div className="blog-details-related-header">
            <span>More in {post.categoryLabel}</span>
            <h2>Related Articles</h2>
          </div>
          <div className="blog-details-related-grid">
            {relatedPosts.map((item) => (
              <Link
                to={`/blogs/${item.slug}`}
                key={item.id}
                className="blog-details-related-card"
              >
                <img src={item.image} alt={item.title} />
                <div>
                  <span>{item.categoryLabel}</span>
                  <h3>{item.title}</h3>
                  <p>{item.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="blog-details-company-wrap">
        <OurCompanies />
      </div>
    </div>
  );
};

export default IconicBlogDetails;
